/**
 * Function to handle the case where thor is not available
 *
 * @param {String} eventType
 * @param {Object} eventArgs
 */
export function thorPushEvent(eventType, eventArgs) {
    if (window.thor) {
        window.thor.pushEvent(eventType, eventArgs);
    }
}

export function eventTypeWithEstateType(eventType, estateType) {
    return `${eventType}_${estateType === 'TRANSACTION_TYPE.RENT' ? 'rent' : 'buy'}`;
}

export function thorFormatPlaceName(placeName) {
    const arrmunRegex = /(\w+)(\s)(0?)(\d{1,2})(\D*)/;

    if (arrmunRegex.test(placeName)) {
        const formatedPlaceName = placeName.replace(arrmunRegex, '$1_$4eme');
        if (formatedPlaceName.includes('1eme')) {
            return formatedPlaceName.replace('eme', 'er');
        }
        return formatedPlaceName;
    }

    return placeName
        .replace(/[ '-]/g, '_')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

export const EstateType = {
    'ITEM_TYPE.AGRICULTURE_FORESTRY': 'av_1',
    'ITEM_TYPE.APARTMENT': 'av_2',
    'ITEM_TYPE.BUILDING': 'av_3',
    'ITEM_TYPE.GASTRONOMY_HOTEL': 'av_4',
    'ITEM_TYPE.HOUSE': 'av_5',
    'ITEM_TYPE.OTHER': 'av_6',
    'ITEM_TYPE.OFFICE': 'av_7',
    'ITEM_TYPE.PARKING': 'av_8',
    'ITEM_TYPE.LAND': 'av_9',
    'ITEM_TYPE.PROJECT': 'av_10',
    'ITEM_TYPE.SENIOR': 'av_11',
    'ITEM_TYPE.STORAGE_PRODUCTION': 'av_12',
    'ITEM_TYPE.SHOP': 'av_13',
};

export const SearchType = {
    'ITEM_TYPE.APARTMENT': 'apartment',
    'ITEM_TYPE.BUILDING': 'building',
    'ITEM_TYPE.GASTRONOMY_HOTEL': 'hotel',
    'ITEM_TYPE.HOUSE': 'house',
    'ITEM_TYPE.OTHER': 'other',
    'ITEM_TYPE.OFFICE': 'office',
    'ITEM_TYPE.PARKING': 'parking',
    'ITEM_TYPE.LAND': 'land',
    'ITEM_TYPE.PROJECT': 'project',
    'ITEM_TYPE.SENIOR': 'senior',
    'ITEM_TYPE.STORAGE_PRODUCTION': 'storage',
    'ITEM_TYPE.SHOP': 'shop',
};

export function thorDistributionType(transactionType) {
    return transactionType === 'TRANSACTION_TYPE.RENT' ? '1' : '2';
}

const mapAvItems = (item, listName, name, transactionType, index) => {
    return {
        list_name: listName,
        id: item.id.toString(),
        index,
        price: item.price,
        estate_type: EstateType[item.item_type.key],
        distribution_type: thorDistributionType(transactionType),
        publication_id: 'MA',
        name,
        zip_code: item.zip || item.place.slug.slice(-5),
        country: 'france',
        city: thorFormatPlaceName(item.place.name),
    };
};

export function thorAvItems(args) {
    const { listing, listings, listName, listsName, name, transactionType } = args;

    const avItems = listings.map((item, index) =>
        mapAvItems(item, listsName || listName, name, transactionType, index),
    );
    if (listing) {
        avItems.unshift(mapAvItems(listing, listName, name, transactionType));
    }

    return avItems;
}
/**
 * Function to map thor page_view user
 * from the dataLayer user and realtor
 *
 * @param {user} user
 * @param {realtor} realtor
 */
function mapUser(user, realtor) {
    return {
        uid: user?.id?.toString(),
        status: user?.authenticated ? 'logged' : 'unlogged',
        agency_id: realtor?.id?.toString(),
        intermediary: realtor?.type,
    };
}

/**
 * Function to get the thor page_view props
 *
 * @param {string} category
 * @param {string} pageTitle
 */
export function getThorViewEventProps(category, pageTitle) {
    return {
        av_user: mapUser(window?.MA_USER, window.MA_REALTOR),
        av_pagetitle: pageTitle,
        av_category: category,
        av_url_path: window.location.pathname,
        av_language: 'fr',
        av_platform: 'web',
        av_site: 'MA',
        av_environment: window.MA.Config?.SENTRY_ENVIRONMENT || 'dev',
        av_zip_code: window.MA.Context?.zipcode,
        av_city: window.MA.Context?.city && thorFormatPlaceName(window.MA.Context?.city),
    };
}

/**
 * Function to push a thor page_view event
 *
 * @param {Object} props
 */
export function thorPushPageViewEvent(pathname) {
    const thorPageviewEnum = {
        '/agence-immobiliere/$': {
            category: 'find_pro',
            title: 'agency_directory_landing',
        },
        '/mandataire-immobiliere/$': {
            category: 'find_pro',
            title: 'representative_directory_landing',
        },
        '/prix-immobilier/.*/rues/': {
            category: 'price_map',
            title: 'price_map_city_streets_index',
        },
        '/prix-immobilier/.*/villes/': {
            category: 'price_map',
            title: 'price_map_subregion_cities_index',
        },
        '/agence-immobiliere/.*/villes/': {
            category: 'find_pro',
            title: 'agency_directory_subregion_streets_index',
        },
        '/achat/$': {
            category: 'classified',
            title: 'classified_search_landing_buy',
        },
        '/location/$': {
            category: 'classified',
            title: 'classified_search_landing_rent',
        },
        '/reussir-votre-vente/': {
            category: 'other',
            title: 'how_to_sell',
        },
        '/comment-estimer-son-bien-immobilier/': {
            category: 'other',
            title: 'how_to_estimate',
        },
        '/comprendre-le-marche-immobilier/': {
            category: 'other',
            title: 'how_to_understand_the_market',
        },
        '/choisir-la-bonne-agence/': {
            category: 'other',
            title: 'how_to_choose_an_agency',
        },
        '^/terms/': {
            category: 'other',
            title: 'terms',
        },
        '/a-propos/terms/': {
            category: 'other',
            title: 'about_terms',
        },
        '/a-propos/presse/': {
            category: 'other',
            title: 'about_press',
        },
        '/a-propos/espace-presse/': {
            category: 'other',
            title: 'about_press_room',
        },
        '/a-propos/equipe/': {
            category: 'other',
            title: 'about_team',
        },
        '/support/faq/': {
            category: 'other',
            title: 'support_faq',
        },
        '/contact/': {
            category: 'other',
            title: 'contact',
        },
    };

    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const key in thorPageviewEnum) {
        const regex = new RegExp(key);
        if (regex.test(pathname)) {
            thorPushEvent(
                'page_view',
                getThorViewEventProps(thorPageviewEnum[key].category, thorPageviewEnum[key].title),
            );
        }
    }
}
